import { create } from 'zustand';
import type { FolderDetails, FolderItems, NewChildItem } from '@/types';

type ItemsUpdater = (prev: FolderDetails['childItems']) => FolderDetails['childItems'];

/**
 * Timeout reference for comment input focus
 * Stored in module scope to persist across store updates
 */
let commentFocusTimeout: ReturnType<typeof setTimeout> | null = null;

/**
 * The ItemStore is used to manage the items and the grouped items.
 * 
 * It will manage the items and the grouped items.
 * 
 * It will also manage the parent id.
 */
type ItemStore = {
  /**
   * The items to display in the table.
   */
  items: FolderDetails['childItems'] | Array<NewChildItem<FolderItems>>;
  /**
   * The function to set the items.
   */
  setItems: (
    items: FolderDetails['childItems'] | Array<NewChildItem<FolderItems>> | ItemsUpdater
  ) => void;
  /**
   * The parent id of the items.
   */
  parentId: string;
  /**
   * The function to set the parent id.
   */
  setParentId: (parentId: string) => void;
  /**
   * The group id of the items.
   */
  groupId: string | null;
  /**
   * The function to set the group id.
   */
  setGroupId: (groupId: string | null ) => void;
  /**
   * The parent itemType of the open folder (e.g., 'FOLDER', 'TASK', 'EPIC_TASK').
   */
  parentItemType?: string | null;
  /**
   * Set the parent itemType (uppercased) for context-aware UI (e.g., Epic-only child restrictions).
   */
  setParentItemType: (itemType: string | null) => void;
  /**
   * The comment id of the items.
   */
  commentId: string | null;
  /**
   * The function to set the comment id.
   */
  setCommentId: (commentId: string | null) => void;
  /**
   * Item currently pending delete confirmation, used for visual highlight.
   */
  deletingItemId: string | null;
  /**
   * Set/clear delete pending highlight item id.
   */
  setDeletingItemId: (itemId: string | null) => void;
};

/**
 * The useItemStore hook is used to manage the items of the folder.
 * 
 * It will manage the items of the folder, by adding temporary items.
 */
const useItemStore = create<ItemStore>((set) => ({
  items: [],
  setItems: (updater) =>
    set((state) => ({
      items:
        typeof updater === 'function' ? (updater as unknown as ItemsUpdater)(state.items) : updater,
    })),
  parentId: '',
  setParentId: (parentId) => set({ parentId }),
  groupId: null,
  setGroupId: (groupId) => set({ groupId: groupId ? String(groupId) : null }),
  parentItemType: null,
  setParentItemType: (itemType) => set({ parentItemType: itemType ? String(itemType).toUpperCase() : null }),
  commentId: null,
  setCommentId: (commentId) => {
    // Clear existing timeout if any
    if (commentFocusTimeout) {
      clearTimeout(commentFocusTimeout);
      commentFocusTimeout = null;
    }
    
    set({ commentId: commentId ? String(commentId) : null });
    
    // Focus comment input after a short delay
    if (commentId) {
      const newCommentInput = document.getElementById('new-message-input') as HTMLTextAreaElement | null;
      if (newCommentInput) {
        commentFocusTimeout = setTimeout(() => {
          newCommentInput.focus();
          commentFocusTimeout = null;
        }, 500);
      }
    }
  },
  deletingItemId: null,
  setDeletingItemId: (itemId) => set({ deletingItemId: itemId ? String(itemId) : null }),
}));

export default useItemStore;
